import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@shared/services/api';
import { useDialog } from '@shared/hooks/useDialog';
import { Card, Badge, Button } from '@shared/components/ui';
import { getStatusTone, toneToBadgeVariant } from '@shared/config/theme';
import {
  Puzzle,
  Plus,
  Trash2,
  Power,
  MessageSquare,
  LifeBuoy,
  X,
  ShieldCheck,
  Zap,
  LayoutGrid,
} from 'lucide-react';

interface Integration {
  id: string;
  integration_type: string;
  name: string;
  enabled: boolean;
  config: any;
  created_at: string;
}

export default function Integrations() {
  const { t, i18n } = useTranslation('admin');
  const { showConfirm } = useDialog();
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newType, setNewType] = useState('zendesk');
  const [newName, setNewName] = useState('');
  const [newKey, setNewKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get('/integrations');
      setIntegrations(res.data.integrations || []);
    } catch {
      setError(t('integrationManagement.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setFormError('');
    try {
      await api.post('/integrations', {
        integration_type: newType,
        name: newName,
        api_key: newKey,
      });
      setShowCreateModal(false);
      setNewName('');
      setNewKey('');
      fetchIntegrations();
    } catch {
      setFormError(t('integrationManagement.errors.createFailed'));
    } finally {
      setCreating(false);
    }
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setNewName('');
    setNewKey('');
    setFormError('');
  };

  const translateProvider = (provider: string) => {
    const key = `integrationManagement.providers.${provider.toLowerCase()}`;
    const translated = t(key);
    return translated === key
      ? provider.replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
      : translated;
  };

  const handleToggle = async (integration: Integration) => {
    try {
      await api.put(`/integrations/${integration.id}`, { enabled: !integration.enabled });
      fetchIntegrations();
    } catch {
      setError(t('integrationManagement.errors.updateFailed'));
    }
  };

  const handleDelete = async (id: string) => {
    const confirmed = await showConfirm(
      t('integrationManagement.confirmDisconnectTitle'),
      t('integrationManagement.confirmDisconnectMessage'),
      'warning'
    );
    if (!confirmed) return;
    try {
      await api.delete(`/integrations/${id}`);
      fetchIntegrations();
    } catch {
      setError(t('integrationManagement.errors.disconnectFailed'));
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'zendesk':
        return <LifeBuoy className="text-brand-600 dark:text-brand-400" />;
      case 'slack':
        return <MessageSquare className="text-accent-text-light dark:text-accent-text-dark" />;
      default:
        return <Puzzle className="text-text-subtle-light dark:text-text-subtle-dark" />;
    }
  };

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-700">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="app-page-title">{t('integrationManagement.title')}</h1>
            <p className="text-text-muted-light dark:text-text-muted-dark font-medium mt-1">
              {t('integrationManagement.description')}
            </p>
          </div>
          <Button
            onClick={() => {
              setFormError('');
              setShowCreateModal(true);
            }}
            size="lg"
            className="shadow-brand-200"
          >
            <Plus className="w-5 h-5 mr-2" strokeWidth={3} />{' '}
            {t('integrationManagement.browseCatalog')}
          </Button>
        </div>

        {error && (
          <Card className="flex flex-col gap-3 border-state-danger-light/20 bg-state-danger-bg-light p-4 text-state-danger-light dark:border-state-danger-dark/20 dark:bg-state-danger-bg-dark dark:text-state-danger-dark sm:flex-row sm:items-center sm:justify-between">
            <p role="alert" className="font-bold">
              {error}
            </p>
            <Button type="button" onClick={fetchIntegrations} variant="outline" size="md">
              {t('integrationManagement.retry')}
            </Button>
          </Card>
        )}

        {loading && integrations.length === 0 ? (
          <div className="py-20 flex justify-center">
            <div className="animate-spin h-12 w-12 border-b-2 border-brand-600 rounded-full"></div>
          </div>
        ) : integrations.length === 0 ? (
          error ? null : (
            <Card className="py-24 text-center border-2 border-dashed">
              <LayoutGrid
                size={48}
                className="mx-auto text-text-subtle-light dark:text-text-subtle-dark mb-4"
              />
              <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                {t('integrationManagement.emptyTitle')}
              </h3>
              <p className="text-text-muted-light dark:text-text-muted-dark mt-2">
                {t('integrationManagement.emptyDescription')}
              </p>
            </Card>
          )
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {integrations.map((integration) => (
              <Card
                key={integration.id}
                className="p-6 transition-all hover:border-brand-200 group"
              >
                <div className="flex justify-between items-start mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-surface-2-light dark:bg-surface-2-dark flex items-center justify-center">
                      {getIcon(integration.integration_type)}
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-slate-900 dark:text-white">
                        {integration.name}
                      </h3>
                      <p className="text-xs font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest">
                        {translateProvider(integration.integration_type)}
                      </p>
                    </div>
                  </div>
                  <Badge
                    variant={
                      toneToBadgeVariant[getStatusTone(integration.enabled ? 'ACTIVE' : 'INACTIVE')]
                    }
                  >
                    {integration.enabled
                      ? t('integrationManagement.active')
                      : t('integrationManagement.paused')}
                  </Badge>
                </div>

                <div className="flex flex-col gap-3 pt-6 border-t border-surface-border-light dark:border-surface-border-dark sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[10px] font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark">
                    <span className="flex items-center gap-1">
                      <ShieldCheck
                        size={12}
                        className="text-state-ok-light dark:text-state-ok-dark"
                      />{' '}
                      {t('integrationManagement.authenticated')}
                    </span>
                    <span>
                      {t('integrationManagement.added', {
                        date: new Intl.DateTimeFormat(
                          i18n.resolvedLanguage || i18n.language
                        ).format(new Date(integration.created_at)),
                      })}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="md"
                      onClick={() => handleToggle(integration)}
                      aria-label={
                        integration.enabled
                          ? t('integrationManagement.pauseIntegration', { name: integration.name })
                          : t('integrationManagement.enableIntegration', { name: integration.name })
                      }
                      title={
                        integration.enabled
                          ? t('integrationManagement.pause')
                          : t('integrationManagement.enable')
                      }
                      className="text-text-subtle-light dark:text-text-subtle-dark hover:text-brand-600 dark:hover:text-brand-400"
                    >
                      <Power size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="md"
                      onClick={() => handleDelete(integration.id)}
                      aria-label={t('integrationManagement.disconnectIntegration', {
                        name: integration.name,
                      })}
                      title={t('integrationManagement.disconnect')}
                      className="text-text-subtle-light dark:text-text-subtle-dark hover:text-state-danger-light dark:hover:text-state-danger-dark"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {showCreateModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="integration-connect-title"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in"
        >
          <Card className="w-full max-w-lg max-h-[calc(100vh-2rem)] overflow-y-auto p-6 sm:p-8 shadow-elevated">
            <div className="flex justify-between items-center mb-8">
              <h2
                id="integration-connect-title"
                className="text-2xl font-black text-slate-900 dark:text-white tracking-tight"
              >
                {t('integrationManagement.connectTitle')}
              </h2>
              <button
                onClick={closeCreateModal}
                aria-label={t('integrationManagement.close')}
                className="min-h-11 min-w-11 inline-flex items-center justify-center rounded-lg hover:bg-surface-2-light dark:hover:bg-surface-2-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              >
                <X className="text-text-subtle-light dark:text-text-subtle-dark" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="space-y-6">
              <div className="space-y-2">
                <span
                  id="integration-provider-label"
                  className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark"
                >
                  {t('integrationManagement.provider')}
                </span>
                <div
                  className="grid grid-cols-2 gap-2"
                  role="group"
                  aria-labelledby="integration-provider-label"
                >
                  {['zendesk', 'slack', 'intercom', 'helpscout'].map((provider) => (
                    <button
                      key={provider}
                      type="button"
                      onClick={() => setNewType(provider)}
                      aria-pressed={newType === provider}
                      className={`min-h-11 p-3 rounded-xl border font-bold text-sm capitalize transition-all ${
                        newType === provider
                          ? 'bg-accent-soft-light dark:bg-accent-soft-dark border-brand-500 text-brand-600 dark:text-brand-400 shadow-sm'
                          : 'bg-surface-card-light dark:bg-surface-card-dark border-surface-border-light dark:border-surface-border-dark text-slate-600 dark:text-slate-300'
                      }`}
                    >
                      {translateProvider(provider)}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label
                  htmlFor="integration-api-key"
                  className="block text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 ml-1"
                >
                  {t('integrationManagement.apiKey')}
                </label>
                <div className="relative">
                  <Zap
                    size={16}
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-subtle-light dark:text-text-subtle-dark"
                  />
                  <input
                    type="password"
                    id="integration-api-key"
                    aria-label={t('integrationManagement.apiKey')}
                    autoComplete="new-password"
                    value={newKey}
                    onChange={(e) => setNewKey(e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400 outline-none"
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="integration-name"
                  className="block text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 ml-1"
                >
                  {t('integrationManagement.connectionName')}
                </label>
                <input
                  id="integration-name"
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  required
                  maxLength={100}
                  autoComplete="organization-title"
                  className="w-full min-h-11 px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-medium focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400 outline-none"
                />
              </div>
              {formError && (
                <div className="rounded-xl border border-state-danger-light/20 bg-state-danger-bg-light p-3 text-sm font-bold text-state-danger-light dark:border-state-danger-dark/20 dark:bg-state-danger-bg-dark dark:text-state-danger-dark">
                  <p role="alert">{formError}</p>
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <Button variant="secondary" className="flex-1" onClick={closeCreateModal}>
                  {t('integrationManagement.cancel')}
                </Button>
                <Button className="flex-1" type="submit" isLoading={creating}>
                  {t('integrationManagement.establishConnection')}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </>
  );
}
